/**
 * Real-time Notification Service
 * Manages WebSocket connections and real-time notifications
 */

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  data: Record<string, any>;
  created_at: string;
  expires_at?: string;
  read: boolean;
}

export interface NotificationMessage {
  type: string;
  notification?: Notification;
  message?: string;
  timestamp?: string;
}

class RealtimeNotificationService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private listeners: Set<(notification: Notification) => void> = new Set();
  private connectionListeners: Set<(connected: boolean) => void> = new Set();

  constructor() {
    this.setupHeartbeat();
  }

  /**
   * Connect to the WebSocket notification service
   */
  async connect(token: string): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const wsUrl = this.getWebSocketUrl(token);
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected for real-time notifications');
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.notifyConnectionChange(true);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: NotificationMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.notifyConnectionChange(false);
        
        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(token);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
      };

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.isConnecting = false;
      this.notifyConnectionChange(false);
    }
  }

  /**
   * Disconnect from the WebSocket service
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
    this.notifyConnectionChange(false);
  }

  /**
   * Send a message to mark a notification as read
   */
  markNotificationAsRead(notificationId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'mark_read',
        notification_id: notificationId
      }));
    }
  }

  /**
   * Add a listener for new notifications
   */
  onNotification(callback: (notification: Notification) => void): () => void {
    this.listeners.add(callback);
    
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Add a listener for connection status changes
   */
  onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionListeners.add(callback);
    
    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: NotificationMessage): void {
    switch (message.type) {
      case 'connection_established':
        console.log('Real-time notifications connected:', message.message);
        break;
        
      case 'notification':
        if (message.notification) {
          this.notifyListeners(message.notification);
          this.showBrowserNotification(message.notification);
        }
        break;
        
      default:
        console.log('Unknown message type:', message.type);
    }
  }

  /**
   * Notify all notification listeners
   */
  private notifyListeners(notification: Notification): void {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Notify all connection status listeners
   */
  private notifyConnectionChange(connected: boolean): void {
    this.connectionListeners.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  /**
   * Show browser notification if permitted
   */
  private showBrowserNotification(notification: Notification): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    // Don't show low priority notifications as browser notifications
    if (notification.priority === 'low') {
      return;
    }

    const browserNotification = new Notification(notification.title, {
      body: notification.message,
      icon: '/favicon.svg',
      tag: notification.id,
      requireInteraction: notification.priority === 'critical'
    });

    // Auto-close after 5 seconds (except critical notifications)
    if (notification.priority !== 'critical') {
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }

    // Handle click on notification
    browserNotification.onclick = () => {
      window.focus();
      browserNotification.close();
      // You could navigate to a specific page here
    };
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(token: string): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        this.connect(token);
      }
    }, delay);
  }

  /**
   * Setup heartbeat to keep connection alive
   */
  private setupHeartbeat(): void {
    setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Send a ping to keep the connection alive
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  private getWebSocketUrl(token: string): string {
    // Environment-aware WebSocket URL configuration
    const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
    let baseUrl: string;
    
    if (environment === 'production') {
      // Production: Use the production API URL
      baseUrl = process.env.REACT_APP_API_URL || 'https://api.trendbeat.twokai.com';
    } else {
      // Development: Use localhost or fallback
      baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    }
    
    const wsUrl = `${baseUrl.replace('http', 'ws')}/api/v1/users/ws/notifications?token=${token}`;
    return wsUrl;
  }
}

// Create singleton instance
export const realtimeNotificationService = new RealtimeNotificationService();

// Export the class for testing
export { RealtimeNotificationService }; 